/**
 * CacheManager - Client-side data caching utility
 * Provides in-memory and localStorage caching with TTL support
 */

class CacheManager {
  constructor() {
    this.memoryCache = new Map();
    this.cachePrefix = 'growguardian_cache_';
  }

  /**
   * Generate cache key with prefix
   */
  getCacheKey(key) {
    return `${this.cachePrefix}${key}`;
  }

  /**
   * Set data in memory cache with TTL
   */
  setMemory(key, data, ttlMinutes = 15) {
    const expiresAt = Date.now() + (ttlMinutes * 60 * 1000);
    this.memoryCache?.set(key, {
      data,
      expiresAt
    });
  }

  /**
   * Get data from memory cache
   */
  getMemory(key) {
    const cached = this.memoryCache?.get(key);
    if (!cached) return null;

    if (Date.now() > cached?.expiresAt) {
      this.memoryCache?.delete(key);
      return null;
    }

    return cached?.data;
  }

  /**
   * Set data in localStorage with TTL
   */
  setStorage(key, data, ttlMinutes = 30) {
    try {
      const cacheKey = this.getCacheKey(key);
      const expiresAt = Date.now() + (ttlMinutes * 60 * 1000);
      const cacheData = {
        data,
        expiresAt
      };
      localStorage.setItem(cacheKey, JSON.stringify(cacheData));
    } catch (error) {
      console.error('Error setting localStorage cache:', error);
    }
  }

  /**
   * Get data from localStorage
   */
  getStorage(key) {
    try {
      const cacheKey = this.getCacheKey(key);
      const cached = localStorage.getItem(cacheKey);
      if (!cached) return null;

      const { data, expiresAt } = JSON.parse(cached);
      
      if (Date.now() > expiresAt) {
        localStorage.removeItem(cacheKey);
        return null;
      }

      return data;
    } catch (error) {
      console.error('Error getting localStorage cache:', error);
      return null;
    }
  }

  /**
   * Get data with fallback from memory -> localStorage
   */
  get(key) {
    return this.getMemory(key) || this.getStorage(key);
  }

  /**
   * Set data in both memory and localStorage
   */
  set(key, data, memoryTTL = 15, storageTTL = 30) {
    this.setMemory(key, data, memoryTTL);
    this.setStorage(key, data, storageTTL);
  }

  /**
   * Clear specific cache entry
   */
  clear(key) {
    this.memoryCache?.delete(key);
    const cacheKey = this.getCacheKey(key);
    localStorage.removeItem(cacheKey);
  }

  /**
   * Clear all cache entries
   */
  clearAll() {
    this.memoryCache?.clear();
    Object.keys(localStorage)?.filter(key => key?.startsWith(this.cachePrefix))?.forEach(key => localStorage.removeItem(key));
  }

  /**
   * Check if cache exists and is valid
   */
  has(key) {
    return this.get(key) !== null;
  }
}

// Export singleton instance
export const cacheManager = new CacheManager();

/**
 * Hook for cached data fetching with React
 */
export const useCachedData = (key, fetchFn, options = {}) => {
  const {
    memoryTTL = 15,
    storageTTL = 30,
    forceRefresh = false
  } = options;

  const fetchData = async () => {
    // Check cache first
    if (!forceRefresh) {
      const cached = cacheManager?.get(key);
      if (cached) return cached;
    }

    // Fetch fresh data
    const data = await fetchFn();
    
    // Cache the result
    cacheManager?.set(key, data, memoryTTL, storageTTL);
    
    return data;
  };

  return fetchData;
};